import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { platformAPI, subscriptionsAPI, fileUrl } from '../../services/api';
import {
  ArrowRight, Check, ChevronDown, Star, ShieldCheck, Sparkles, Menu, X,
  FileText, ReceiptText, BarChart3, Landmark, Package, Users,
  PlayCircle, Lock, Zap, Globe, Headphones,
} from 'lucide-react';
import { LandingHeader, LandingFooter } from './LandingChrome';
import ChatWidget from '../common/ChatWidget';
import '../superAdmin/superAdmin.css';
import '../../styles/landing.css';


const FALLBACK_CONTENT = {
  brand_name: 'LedgerPro',
  hero: {
    badge: 'New · AI-powered insights are here',
    title: 'Accounting software that works as hard as you do',
    title_accent: 'as hard as you do',
    subtitle: 'Organize your books, send professional invoices, track expenses and see exactly where your business stands — all in one place.',
    primary_cta_text: 'Start free for 14 days',
    primary_cta_url: '/register',
    secondary_cta_text: 'View pricing',
    secondary_cta_url: '#pricing',
    bullets: ['Free 14-day trial — no credit card required', 'Cancel anytime', 'Set up in under 10 minutes'],
  },
  stats: [],
  features: [],
  showcase: [],
  pricing: { eyebrow: 'Pricing', title: 'Simple plans that grow with you', subtitle: 'Start free for 14 days.', highlight_slug: 'professional' },
  testimonials: [],
  faq: [],
  cta_banner: {},
  footer: { about: '', columns: [] },
};

const FEATURE_ICONS = { FileText, ReceiptText, BarChart3, Landmark, Package, Users };

/* ---------- SEO head manager ---------- */
function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
}

function SeoManager({ seo, brandName, logoPath }) {
  useEffect(() => {
    if (!seo) return;
    const url = window.location.origin + '/';
    document.title = seo.title || `${brandName} — Professional Accounting Software`;

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
    upsertMeta('name', 'author', brandName);

    // Open Graph
    upsertMeta('property', 'og:site_name', brandName);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    if (seo.og_image_path) {
      upsertMeta('property', 'og:image', fileUrl(seo.og_image_path));
      upsertMeta('property', 'og:image:width', '1200');
      upsertMeta('property', 'og:image:height', '630');
    }

    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    if (seo.og_image_path) upsertMeta('name', 'twitter:image', fileUrl(seo.og_image_path));

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Structured data (JSON-LD)
    const ldId = 'ld-json-landing';
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: brandName,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: seo.description,
        url,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: '14-day free trial' },
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '2417' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: brandName,
        url,
        logo: logoPath ? fileUrl(logoPath) : (seo.og_image_path ? fileUrl(seo.og_image_path) : undefined),
      },
    ]);
  }, [seo, brandName]);
  return null;
}

/* ---------- Dashboard mockup (pure CSS visual) ---------- */
function HeroMockup() {
  return (
    <div className="lp-mockup" aria-hidden="true">
      <div className="lp-mockup-bar">
        <span /><span /><span />
        <div className="lp-mockup-pill">app.ledgerpro.com/dashboard</div>
      </div>
      <div className="lp-mockup-body">
        <div className="lp-mockup-side">
          <div className="lp-mockup-nav-item active" />
          <div className="lp-mockup-nav-item" />
          <div className="lp-mockup-nav-item" />
          <div className="lp-mockup-nav-item" />
        </div>
        <div className="lp-mockup-main">
          <div className="lp-mockup-kpis">
            <div className="lp-kpi">
              <span className="lp-kpi-label">Revenue</span>
              <span className="lp-kpi-value">$48,290</span>
              <span className="lp-kpi-delta up">▲ 18%</span>
              <div className="lp-kpi-spark"><i style={{ height: '30%' }} /><i style={{ height: '45%' }} /><i style={{ height: '38%' }} /><i style={{ height: '62%' }} /><i style={{ height: '55%' }} /><i style={{ height: '80%' }} /></div>
            </div>
            <div className="lp-kpi">
              <span className="lp-kpi-label">Net Profit</span>
              <span className="lp-kpi-value">$16,410</span>
              <span className="lp-kpi-delta up">▲ 9%</span>
              <div className="lp-kpi-spark green"><i style={{ height: '35%' }} /><i style={{ height: '50%' }} /><i style={{ height: '42%' }} /><i style={{ height: '58%' }} /><i style={{ height: '70%' }} /><i style={{ height: '88%' }} /></div>
            </div>
          </div>
          <div className="lp-mockup-chart">
            <div className="lp-chart-head"><b>Cash Flow</b><span>This year</span></div>
            <div className="lp-chart-bars">
              {[42, 65, 52, 78, 60, 90, 72, 96, 68].map((h, i) => (
                <div key={i} className={`lp-bar ${h > 75 ? 'hot' : ''}`} style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
          <div className="lp-mockup-invoice">
            <div className="lp-inv-row done"><Check size={12} /> Invoice #1042 · paid · $2,450</div>
            <div className="lp-inv-row pending"><span className="dot" /> Invoice #1043 · awaiting · $1,180</div>
          </div>
        </div>
      </div>
      <div className="lp-float-card lp-float-a"><Zap size={14} /> Books reconciled automatically</div>
      <div className="lp-float-card lp-float-b"><ShieldCheck size={14} /> Bank-level security</div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ seo: null, content: FALLBACK_CONTENT });
  const [platform, setPlatform] = useState({});
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [faqOpen, setFaqOpen] = useState(0);

  useEffect(() => {
    platformAPI.getLanding().then(d => setData(d)).catch(() => {});
    platformAPI.getPublicSettings().then(p => setPlatform(p)).catch(() => {});
    subscriptionsAPI.plansPublic().then(p => setPlans(p.filter(pl => pl.is_active !== 0 && pl.is_active !== false))).catch(() => {});
  }, []);

  const c = data.content || FALLBACK_CONTENT;
  const brandName = c.brand_name || 'LedgerPro';

  const isCompanyUser = !!user?.company_id;
  const isPlatformUser = !!user && !user.company_id;

  const fmtPrice = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="lp-page">
      <SeoManager seo={data.seo} brandName={brandName} logoPath={platform.logo_path} />

      {/* ================= HEADER ================= */}
      <LandingHeader />

      {/* ================= HERO ================= */}
      <section className="lp-hero">
        <div className="lp-container lp-hero-grid">
          <div className="lp-hero-copy">
            {c.hero?.badge && <div className="lp-badge"><Sparkles size={13} /> {c.hero.badge}</div>}
            <h1 className="lp-h1">
              {c.hero?.title_accent && c.hero.title.includes(c.hero.title_accent) ? (
                <>
                  {c.hero.title.split(c.hero.title_accent)[0]}
                  <em>{c.hero.title_accent}</em>
                  {c.hero.title.split(c.hero.title_accent)[1]}
                </>
              ) : c.hero?.title}
            </h1>
            <p className="lp-sub">{c.hero?.subtitle}</p>
            <div className="lp-hero-actions">
              <Link to={isCompanyUser || isPlatformUser ? '/dashboard' : (c.hero?.primary_cta_url || '/register')} className="lp-btn lp-btn-primary lp-btn-lg">
                {isCompanyUser || isPlatformUser ? 'Go to Dashboard' : c.hero?.primary_cta_text} <ArrowRight size={17} />
              </Link>
              <a href={c.hero?.secondary_cta_url || '#pricing'} className="lp-btn lp-btn-outline lp-btn-lg">
                <PlayCircle size={17} /> {c.hero?.secondary_cta_text}
              </a>
            </div>
            <ul className="lp-hero-bullets">
              {(c.hero?.bullets || []).map((b, i) => <li key={i}><Check size={15} /> {b}</li>)}
            </ul>
          </div>
          <HeroMockup />
        </div>

        {/* trust strip */}
        <div className="lp-container">
          <div className="lp-trust-strip">
            <span>Trusted by teams at</span>
            <div className="lp-trust-logos">
              <b>Brightline</b><b>NorthPeak</b><b>Vantage&nbsp;Co.</b><b>AtlasWorks</b><b>Sunrise&nbsp;Ltd.</b><b>Meridian</b>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      {(c.stats || []).length > 0 && (
        <section className="lp-stats">
          <div className="lp-container lp-stats-grid">
            {c.stats.map((s, i) => (
              <div key={i} className="lp-stat">
                <div className="lp-stat-value">{s.value}</div>
                <div className="lp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ================= FEATURES ================= */}
      <section id="features" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-eyebrow">Everything in one place</span>
            <h2>Powerful tools for every part of your business</h2>
            <p>Invoicing, expenses, banking, inventory and reporting — built on a professional double-entry engine your accountant will approve of.</p>
          </div>
          <div className="lp-features-grid">
            {(c.features || []).map((f, i) => {
              const Icon = FEATURE_ICONS[f.icon] || Zap;
              return (
                <div key={i} className="lp-feature-card">
                  <div className="lp-feature-icon"><Icon size={22} /></div>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= SHOWCASE ================= */}
      <section id="showcase" className="lp-section lp-section-alt">
        <div className="lp-container">
          {(c.showcase || []).map((s, i) => (
            <div key={i} className={`lp-showcase ${i % 2 === 1 ? 'reverse' : ''}`}>
              <div className="lp-showcase-copy">
                <span className="lp-eyebrow">{s.eyebrow}</span>
                <h2>{s.title}</h2>
                <p>{s.description}</p>
                <ul className="lp-check-list">
                  {(s.bullets || []).map((b, j) => (
                    <li key={j}><span className="lp-check-dot"><Check size={13} /></span>{b}</li>
                  ))}
                </ul>
              </div>
              <div className="lp-showcase-visual">
                <div className={`lp-panel lp-panel-${(i % 3) + 1}`} aria-hidden="true">
                  <div className="lp-panel-title">{s.eyebrow === 'Bookkeeping' ? 'General Ledger' : s.eyebrow === 'Cash flow' ? 'Invoice #1042' : 'Profit & Loss'}</div>
                  {s.eyebrow === 'Bookkeeping' && (
                    <table className="lp-mini-table">
                      <thead><tr><th>Date</th><th>Account</th><th>Debit</th><th>Credit</th></tr></thead>
                      <tbody>
                        <tr><td>Aug 21</td><td>Cash</td><td>$2,450</td><td>—</td></tr>
                        <tr><td>Aug 21</td><td>Sales Revenue</td><td>—</td><td>$2,450</td></tr>
                        <tr><td>Aug 20</td><td>A/R</td><td>$1,180</td><td>—</td></tr>
                        <tr><td>Aug 20</td><td>Sales Revenue</td><td>—</td><td>$1,180</td></tr>
                      </tbody>
                    </table>
                  )}
                  {s.eyebrow === 'Cash flow' && (
                    <div className="lp-invoice-preview">
                      <div className="lp-inv-line total">Total due<span>$2,450.00</span></div>
                      <button className="lp-inv-paybtn" type="button">Pay now</button>
                      <div className="lp-inv-note">Recurring · sent automatically</div>
                    </div>
                  )}
                  {s.eyebrow !== 'Bookkeeping' && s.eyebrow !== 'Cash flow' && (
                    <div className="lp-pl-rows">
                      {[['Revenue', '$48,290', 'pos'], ['COGS', '$19,110', 'neg'], ['Gross Profit', '$29,180', ''], ['Expenses', '$12,770', 'neg'], ['Net Profit', '$16,410', 'strong']].map(r => (
                        <div key={r[0]} className={`lp-pl-row ${r[2]}`}><span>{r[0]}</span><b>{r[1]}</b></div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head">
            <span className="lp-eyebrow">{c.pricing?.eyebrow || 'Pricing'}</span>
            <h2>{c.pricing?.title || 'Simple plans that grow with you'}</h2>
            <p>{c.pricing?.subtitle}</p>
            <div className="lp-cycle-toggle">
              <button type="button" className={cycle === 'monthly' ? 'active' : ''} onClick={() => setCycle('monthly')}>Monthly</button>
              <button type="button" className={cycle === 'yearly' ? 'active' : ''} onClick={() => setCycle('yearly')}>
                Yearly <small>{c.pricing?.save_note?.match(/\d+%|17%/)?.[0] || 'Save 17%'}</small>
              </button>
            </div>
          </div>

          <div className="lp-pricing-grid">
            {plans.map(plan => {
              const highlighted = plan.slug === (c.pricing?.highlight_slug || 'professional');
              const isTrial = plan.slug === 'free-trial';
              const price = cycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
              let features = [];
              try { features = JSON.parse(plan.features || '[]'); } catch { /* */ }
              return (
                <div key={plan.id} className={`lp-price-card ${highlighted ? 'featured' : ''}`}>
                  {highlighted && <div className="lp-price-flag">Most popular</div>}
                  {isTrial && <div className="lp-price-flag trial">14-day free trial</div>}
                  <h3>{plan.name}</h3>
                  <div className="lp-price-amount">
                    {isTrial ? <><span className="lp-price-num">Free</span></> : (
                      <>
                        <span className="lp-price-cur">$</span>
                        <span className="lp-price-num">{fmtPrice(price).replace('$', '')}</span>
                        <span className="lp-price-per">/{cycle === 'yearly' ? 'yr' : 'mo'}</span>
                      </>
                    )}
                  </div>
                  <p className="lp-price-desc">{plan.description}</p>
                  <ul className="lp-price-features">
                    {(features.length ? features : [
                      `${plan.max_users} users`,
                      `${plan.max_invoices_month === -1 ? 'Unlimited' : plan.max_invoices_month} invoices/month`,
                      `${plan.max_storage_mb >= 1024 ? plan.max_storage_mb / 1024 + ' GB' : plan.max_storage_mb + ' MB'} storage`,
                    ]).slice(0, 6).map((f, i) => <li key={i}><Check size={15} /> {f}</li>)}
                  </ul>
                  <Link to={`/register${plan.id ? `?plan=${plan.id}` : ''}`} className={`lp-btn ${highlighted ? 'lp-btn-primary' : 'lp-btn-outline'} lp-btn-block`}>
                    {isTrial ? 'Start free trial' : `Choose ${plan.name}`}
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="lp-pricing-note"><Lock size={13} /> All plans include a 14-day free trial · No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      {(c.testimonials || []).length > 0 && (
        <section className="lp-section lp-section-alt">
          <div className="lp-container">
            <div className="lp-section-head">
              <span className="lp-eyebrow">Customer stories</span>
              <h2>Loved by businesses everywhere</h2>
            </div>
            <div className="lp-testimonials-grid">
              {c.testimonials.map((tst, i) => (
                <figure key={i} className="lp-testimonial">
                  <div className="lp-stars">{[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" strokeWidth={0} />)}</div>
                  <blockquote>“{tst.quote}”</blockquote>
                  <figcaption>
                    <span className="lp-avatar">{tst.name?.[0]}</span>
                    <span>
                      <b>{tst.name}</b>
                      <small>{tst.role}{tst.company ? ` · ${tst.company}` : ''}</small>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= FAQ ================= */}
      {(c.faq || []).length > 0 && (
        <section id="faq" className="lp-section">
          <div className="lp-container lp-faq-wrap">
            <div className="lp-section-head">
              <span className="lp-eyebrow">FAQ</span>
              <h2>Frequently asked questions</h2>
            </div>
            <div className="lp-faq-list">
              {c.faq.map((f, i) => (
                <div key={i} className={`lp-faq-item ${faqOpen === i ? 'open' : ''}`}>
                  <button type="button" className="lp-faq-q" onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}>
                    {f.q}
                    <ChevronDown size={17} />
                  </button>
                  <div className="lp-faq-a"><p>{f.a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= CTA BANNER ================= */}
      <section className="lp-cta-banner">
        <div className="lp-container lp-cta-inner">
          <h2>{c.cta_banner?.title || 'Ready to take control of your finances?'}</h2>
          <p>{c.cta_banner?.subtitle}</p>
          <Link to={(isCompanyUser || isPlatformUser) ? '/dashboard' : (c.cta_banner?.button_url || '/register')} className="lp-btn lp-btn-white lp-btn-lg">
            {(isCompanyUser || isPlatformUser) ? 'Go to Dashboard' : (c.cta_banner?.button_text || 'Start free')} <ArrowRight size={17} />
          </Link>
          <div className="lp-cta-trust">
            <span><Headphones size={14} /> Award-winning support</span>
            <span><Globe size={14} /> Trusted worldwide</span>
            <span><ShieldCheck size={14} /> Bank-level security</span>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <LandingFooter content={c} brandName={brandName} />

      {/* Live chat / support assistant */}
      <ChatWidget />
    </div>
  );
}
